import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

export type AppMode = "ai" | "generic";

const STORAGE_KEY = "pickthree-mode-override";

// Check if we're in development mode - localStorage override only works in dev
const isDev = import.meta.env.DEV;

interface AppModeContextValue {
  mode: AppMode;
  isGeneric: boolean;
  setModeOverride: (mode: AppMode | null) => void;
  modeOverride: AppMode | null;
}

const AppModeContext = createContext<AppModeContextValue | null>(null);

/**
 * Detect the app mode based on the current domain.
 * - pickthree.me (or www.pickthree.me, *.pickthree.me) -> generic mode
 * - pickthree.ai (or any other domain) -> ai mode
 */
function detectModeFromDomain(): AppMode {
  const hostname = window.location.hostname.toLowerCase();
  
  // Check for pickthree.me domain (exact match or any subdomain)
  const isGenericDomain = 
    hostname === "pickthree.me" || 
    hostname.endsWith(".pickthree.me");
  
  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log("[AppMode] Hostname:", hostname, "-> Mode:", isGenericDomain ? "generic" : "ai");
  }
  
  return isGenericDomain ? "generic" : "ai";
}

/**
 * Get the mode override from localStorage (for dev testing only).
 * Returns null in production - only domain detection applies.
 */
function getStoredModeOverride(): AppMode | null {
  // Only allow override in development mode
  if (!isDev) return null;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "ai" || stored === "generic") {
      return stored;
    }
  } catch {
    // localStorage may not be available
  }
  return null;
}

/**
 * Store the mode override in localStorage (dev only).
 */
function setStoredModeOverride(mode: AppMode | null): void {
  // Only allow override in development mode
  if (!isDev) return;
  
  try {
    if (mode === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  } catch {
    // localStorage may not be available
  }
}

export function AppModeProvider({ children }: { children: ReactNode }) {
  const [modeOverride, setModeOverrideState] = useState<AppMode | null>(() => getStoredModeOverride());
  const [detectedMode] = useState<AppMode>(() => detectModeFromDomain());

  // The effective mode: in dev, override takes precedence; in prod, always use detected mode
  const mode = isDev ? (modeOverride ?? detectedMode) : detectedMode;
  const isGeneric = mode === "generic";

  // Debug logging
  if (isDev) {
    console.log("[AppMode] Detected:", detectedMode, "Override:", modeOverride, "Effective:", mode);
  }

  const setModeOverride = (newMode: AppMode | null) => {
    // Only allow override in development mode
    if (!isDev) return;
    
    setModeOverrideState(newMode);
    setStoredModeOverride(newMode);
  };

  // Sync with localStorage changes from other tabs (dev only)
  useEffect(() => {
    if (!isDev) return;
    
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const newValue = e.newValue;
        if (newValue === "ai" || newValue === "generic") {
          setModeOverrideState(newValue);
        } else {
          setModeOverrideState(null);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <AppModeContext.Provider value={{ mode, isGeneric, setModeOverride, modeOverride }}>
      {children}
    </AppModeContext.Provider>
  );
}

export function useAppMode() {
  const context = useContext(AppModeContext);
  if (!context) {
    throw new Error("useAppMode must be used within an AppModeProvider");
  }
  return context;
}

